import { useMutation, useQueryClient } from '@tanstack/react-query';
import { documentWorkflowControllerRemoveSignerMutation } from '@dealstack/shared/lib/backendClient/@tanstack/react-query.gen';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { getBackendErrorMessage } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	buildInvestmentProgrammeDocumentQueryKey,
	buildInvestmentProgrammeDocumentsListKeyPrefix,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/documentQueryKeys';
import { getDocumentWorkflowEventsQueryKey } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useDocumentWorkflowEvents';

interface Options {
	dealId: string;
	programmeId: string;
	documentWorkflowInstanceId: string;
	onSuccess?: () => void;
}

/**
 * Removes one outstanding signature requirement. `dealId` is not a path param on this
 * route (the backend client injects it as the `deal-id` header) but is still needed to invalidate
 * the right document query keys. The list key goes too: removal can complete the document, which
 * changes its row in the documents table.
 */
export const useRemoveSigner = ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	onSuccess,
}: Options) => {
	const tMipManage = useTranslationMipManage();
	const queryClient = useQueryClient();

	const mutation = useMutation({
		...documentWorkflowControllerRemoveSignerMutation(),
		onSuccess: async () => {
			showToastV2({
				state: 'success',
				title: tMipManage('documents_page.remove_signer.toast.success'),
			});
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: buildInvestmentProgrammeDocumentQueryKey({
						dealId,
						programmeId,
						documentWorkflowInstanceId,
					}),
				}),
				queryClient.invalidateQueries({
					queryKey: buildInvestmentProgrammeDocumentsListKeyPrefix({
						dealId,
						programmeId,
					}),
				}),
				queryClient.invalidateQueries({
					queryKey: getDocumentWorkflowEventsQueryKey({
						programmeId,
						documentWorkflowInstanceId,
					}),
				}),
			]);
			onSuccess?.();
		},
		// The refusal `code` is stripped by GlobalExceptionFilter, but REFUSAL_MESSAGES' sentence
		// survives as `error.message` — surface it so the manager learns why.
		onError: (error) => {
			showToastV2({
				state: 'error',
				title: tMipManage('documents_page.remove_signer.toast.error'),
				message: getBackendErrorMessage(error),
			});
		},
	});

	return {
		remove: (signerId: string) =>
			mutation.mutate({
				path: { programmeId, documentWorkflowInstanceId },
				body: { signerId },
			}),
		isPending: mutation.isPending,
	};
};
